import {useCallback, useEffect, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {RootState} from '../../redux/store';
import {getIndex, runNlpSearch} from '../../api/EcaServices';
import {SSE_ENDPOINT_ECA, SSE_SERVICE_TYPE} from '../../constants/constants';
import {useToast} from '../../contexts/toastContext/ToastContext';
import {t} from 'i18next';

export const useNlpSearch = (indexName = '') => {
  const clientId = useSelector(
    (state: RootState) => state.selectedClient.clientId
  );
  const projectId = useSelector((state: RootState) => state.reports.projectId);
  const [isIndexing, setIsIndexing] = useState(false);
  const [searchID, setSearchID] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSearchCancelled, setSearchCancel] = useState(false);
  const eventSourceRef = useRef<EventSource | null>(null);
  const {showToast} = useToast();
  const abortControllers: Map<string, AbortController> = new Map();

  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
      abortControllers.forEach((controller) => {
        controller.abort();
      });
      abortControllers.clear();
    };
  }, []);

  const fetchIndex = useCallback(async () => {
    try {
      setIsIndexing(false);
      const params = `?clientId=${clientId}&projectId=${projectId}`;
      const result = await getIndex(params);
      setIsIndexing(
        result.data.data.find((index: any) => index?.type === 'Nlp')?.status ===
          'InProgress'
      );
    } catch (e) {
      console.error('Error', e);
    }
  }, [clientId, projectId]);

  const runSearch = useCallback(
    async (searchQuery = '') => {
      const params = `?clientId=${clientId}&projectId=${projectId}`;
      const url = `/indexes/${indexName}/searches/nlp/run` + params;
      try {
        setIsLoading(true);
        setSearchID('');
        setSearchCancel(false);
        if (abortControllers.has(url)) {
          abortControllers.get(url)?.abort();
        }
        const controller = new AbortController();
        abortControllers.set(url, controller);
        const result = await runNlpSearch(
          params,
          indexName,
          {
            nlpQuery: searchQuery,
          },
          controller.signal
        );
        getSearchIdSse(result.data.data.requestId);
        abortControllers.delete(url);
      } catch (e) {
        setIsLoading(false);
        abortControllers.delete(url);
        console.error('Error', e);
      }
    },
    [clientId, indexName, projectId]
  );

  const cancelSearch = useCallback(() => {
    setSearchCancel(true);
    setIsLoading(false);
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }
    abortControllers.forEach((controller) => {
      controller.abort();
    });
  }, []);

  const getSearchIdSse = (requestId: string) => {
    const SSE_URL = `${SSE_ENDPOINT_ECA}/sse/${requestId}/${SSE_SERVICE_TYPE.NLP_SEARCH}`;
    eventSourceRef.current = new EventSource(SSE_URL);
    eventSourceRef.current.onmessage = (event) => {
      const responseData = JSON.parse(event.data);
      const isCompleted = responseData.status.toLowerCase() == 'completed';
      const isFailed = responseData.status.toLowerCase() == 'failed';
      if (isCompleted) {
        setSearchID(responseData.data.SearchId);
      } else if (isFailed) {
        showToast(t('ecaDocList.failedText'), 'error', undefined, 'center');
      }
      
      if (isCompleted || isFailed) {
        setIsLoading(false);
        if (eventSourceRef.current) {
          eventSourceRef.current.close();
        }
      }
    };

    eventSourceRef.current.onerror = (error) => {
      console.error('EventSource failed:', error);
      setIsLoading(false);
      showToast(t('ecaDocList.failedText'), 'error', undefined, 'center');
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  };

  return {
    isIndexing,
    isLoading,
    searchID,
    isSearchCancelled,
    fetchIndex,
    runSearch,
    cancelSearch,
  };
};
